import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, I18N_STORAGE_KEY } from "@i18n";

// ═══════════════════════════════════════════════════════════
// LanguageSwitcher — Sidebar language selector
// Collapsed: icon-only with tooltip. Expanded: full dropdown.
// ═══════════════════════════════════════════════════════════

export function LanguageSwitcher({ collapsed }: { collapsed?: boolean }) {
  const { i18n, t } = useTranslation();

  const handleChange = (lang: string) => {
    i18n.changeLanguage(lang);
    // Persist manual choice so it takes priority over B24 LANG param on next load
    try {
      localStorage.setItem(I18N_STORAGE_KEY, lang);
    } catch {
      /* noop */
    }
  };

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="mx-auto flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={() => {
              // Cycle through languages
              const idx = SUPPORTED_LANGUAGES.indexOf(
                i18n.language as (typeof SUPPORTED_LANGUAGES)[number],
              );
              const next = SUPPORTED_LANGUAGES[(idx + 1) % SUPPORTED_LANGUAGES.length];
              if (next) handleChange(next);
            }}
            type="button"
          >
            <Globe className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>
            {t("language.label")}:{" "}
            {LANGUAGE_LABELS[i18n.language as keyof typeof LANGUAGE_LABELS] ?? i18n.language}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Select value={i18n.language} onValueChange={handleChange}>
      <SelectTrigger className="w-full bg-transparent border-none shadow-none hover:bg-primary/10 hover:text-primary focus:ring-0 focus:ring-offset-0 text-secondary text-[14px] font-medium h-9 px-3 transition-colors">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 shrink-0" />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <SelectItem key={lang} value={lang}>
            {LANGUAGE_LABELS[lang]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
